import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ISD_CASES, ISD_CASE_LABELS } from "./rsaPathTypes";
import "./IntersectionPanelContent.css";

// Point-tool flow is gone — ISD marks always go through the form flow
// (select lane → Create button → form → click-drag to place). Move + Measure
// are the only standalone drawing tools left.
const DRAWING_MODES = [
  {
    value: null,
    label: "Move",
    icon: "fa-solid fa-hand-back-fist",
    title: "Pan/move map (no drawing)",
  },
  {
    value: "measure-distance",
    label: "Measure",
    icon: "fa-ruler",
    title: "Measure distance (click points on map)",
  },
];

/**
 * Intersection panel content: lane/path picker, ISD creation form, ISD list,
 * and Properties editor.
 */
export default function IntersectionPanelContent({
  drawingMode = null,
  onDrawingModeChange,
  // Generic overlays (measure lines, etc.)
  drawnOverlays = [],
  onClearOverlays,
  onOverlayUpdate,
  onOverlayRemove,
  // ISD intersections from state
  intersections = [],
  activeIntersectionId = null,
  onUpdateIntersection,
  onDeleteIntersection,
  onSetActiveIntersection,
  // Paths + lanes (for the picker and the read-only RSA Path field)
  paths = [],
  lanes = [],
  // Pending creation draft — kept at RSAAnalysis level so the drawing hook
  // can read it and the placement click-drag can merge its fields.
  pendingIsdDraft = null,
  onSetPendingIsdDraft,
  // F-case opposite-block picker — owned by RSAAnalysis. Calling
  // `onStartOppositePointPick(callback)` puts the map in click-drag mode; the
  // callback is invoked once with `{ insertionPoint, azimuth, arrowLengthFeet }`
  // when the user releases the drag.
  oppositePointPickActive = false,
  onStartOppositePointPick,
  onCancelOppositePointPick,
  // Lifted draft for the opposite block — written by the picker callback and
  // by drag handles on the cyan block, read by the map overlay so the cyan
  // block stays visible while the form is open.
  oppositeBlockDraft = null,
  onUpdateOppositeBlockDraft,
  // Re-fetch paths/lanes from the backend on demand.
  onRefreshPathsAndLanes,
  // Per-row visibility toggles — wired by RSAAnalysis to the same reducer
  // actions PathsListPanel uses, so map overlays update immediately.
  onTogglePathVisible,
  onToggleLaneVisible,
  // Lifted lane/path selection — when provided, the component stays in sync
  // with the parent (so PathSelectorMap can draw the selected target as a
  // highlighted overlay). Falls back to local state when the parent doesn't
  // pass these props.
  selectedTargetId: selectedTargetIdProp = null,
  onSetSelectedTargetId,
}) {
  const [selectedTargetIdLocal, setSelectedTargetIdLocal] = useState(null);
  const selectedTargetId = onSetSelectedTargetId
    ? selectedTargetIdProp
    : selectedTargetIdLocal;
  const setSelectedTargetId = onSetSelectedTargetId ?? setSelectedTargetIdLocal;
  const [formOpen, setFormOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  // Toggle for the .isd-properties-panel under the active row.
  const [propertiesOpen, setPropertiesOpen] = useState(true);

  // Form fields
  const [formIntersectionName, setFormIntersectionName] = useState("");
  const [formManeuverIndex, setFormManeuverIndex] = useState(1);
  const [formIsdCase, setFormIsdCase] = useState(ISD_CASES.LEFT_TURN);
  const [formSecondaryRoute, setFormSecondaryRoute] = useState("");

  // If the user switches the case dropdown away from F while a pick is in
  // flight, cancel the pick AND drop any in-progress draft so the cyan block
  // doesn't linger on the map for a non-F intersection.
  useEffect(() => {
    if (formIsdCase !== ISD_CASES.F_CASE) {
      if (oppositePointPickActive) onCancelOppositePointPick?.();
      if (oppositeBlockDraft) onUpdateOppositeBlockDraft?.(null);
    }
  }, [
    formIsdCase,
    oppositePointPickActive,
    oppositeBlockDraft,
    onCancelOppositePointPick,
    onUpdateOppositeBlockDraft,
  ]);

  const activeIntersection =
    intersections.find((i) => i.id === activeIntersectionId) ?? null;

  // Flat list: each path followed by its lanes.
  const targets = useMemo(() => {
    const out = [];
    paths.forEach((p) => {
      out.push({
        id: p.id,
        label: p.label,
        kind: "path",
        visible: p.visible !== false,
      });
      lanes
        .filter((l) => l.pathId === p.id)
        .forEach((l) =>
          out.push({
            id: l.id,
            label: l.label,
            kind: "lane",
            visible: l.visible !== false,
          }),
        );
    });
    return out;
  }, [paths, lanes]);

  const selectedTarget = useMemo(() => {
    const t = targets.find((tt) => tt.id === selectedTargetId);
    // Only lanes are valid ISD targets — if the selected id resolves to a
    // path (e.g. legacy state), treat it as no selection so Create stays off.
    return t && t.kind === "lane" ? t : null;
  }, [targets, selectedTargetId]);

  const nextManeuverIndex = useMemo(
    () =>
      intersections.reduce((max, i) => Math.max(max, i.maneuverIndex || 0), 0) +
      1,
    [intersections],
  );

  const openForm = useCallback(() => {
    setFormIntersectionName("");
    setFormManeuverIndex(nextManeuverIndex);
    setFormIsdCase(ISD_CASES.LEFT_TURN);
    setFormSecondaryRoute("");
    onUpdateOppositeBlockDraft?.(null);
    setFormOpen(true);
  }, [nextManeuverIndex, onUpdateOppositeBlockDraft]);

  const isFormValid = useMemo(
    () =>
      !!selectedTargetId &&
      formIntersectionName.trim().length > 0 &&
      Number.isFinite(formManeuverIndex) &&
      formManeuverIndex >= 1 &&
      !!formIsdCase &&
      formSecondaryRoute.trim().length > 0,
    [
      selectedTargetId,
      formIntersectionName,
      formManeuverIndex,
      formIsdCase,
      formSecondaryRoute,
    ],
  );

  const handleFormConfirm = useCallback(() => {
    if (!isFormValid) return;
    // The opposite-block draft is bundled into the intersection by RSAAnalysis
    // when the placement click-drag commits — we don't pass it through the
    // pending-draft envelope.
    onSetPendingIsdDraft?.({
      intersectionName: formIntersectionName,
      maneuverIndex: formManeuverIndex,
      isdCase: formIsdCase,
      rsaPathId: selectedTargetId,
      secondaryRoute: formSecondaryRoute,
    });
    setFormOpen(false);
  }, [
    isFormValid,
    onSetPendingIsdDraft,
    formIntersectionName,
    formManeuverIndex,
    formIsdCase,
    selectedTargetId,
    formSecondaryRoute,
  ]);

  const handleFormCancel = useCallback(() => {
    if (oppositePointPickActive) onCancelOppositePointPick?.();
    onUpdateOppositeBlockDraft?.(null);
    setFormOpen(false);
  }, [
    oppositePointPickActive,
    onCancelOppositePointPick,
    onUpdateOppositeBlockDraft,
  ]);
  const handleDraftCancel = useCallback(() => {
    onSetPendingIsdDraft?.(null);
    onUpdateOppositeBlockDraft?.(null);
  }, [onSetPendingIsdDraft, onUpdateOppositeBlockDraft]);

  const handleFieldChange = useCallback(
    (field, value) => {
      if (!activeIntersectionId) return;
      onUpdateIntersection?.(activeIntersectionId, { [field]: value });
    },
    [activeIntersectionId, onUpdateIntersection],
  );

  const handleCopyAll = useCallback(() => {
    const data = intersections.map((i) => ({
      intersectionName: i.intersectionName,
      maneuverIndex: i.maneuverIndex,
      isdCase: i.isdCase,
      rsaPathId: i.rsaPathId,
      secondaryRoute: i.secondaryRoute,
      insertionPoint: i.insertionPoint,
      azimuth: i.azimuth,
      oppositePoint: i.oppositePoint,
    }));
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopiedId("all");
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, [intersections]);

  return (
    <div className="paths-panel-mode-content paths-panel-mode-content-intersection">
      {/* ── Pending placement banner ── */}
      {pendingIsdDraft && (
        <div className="isd-pending-banner" role="status">
          <i className="fa-solid fa-crosshairs" aria-hidden />
          <span>Click & drag on map to place ISD mark</span>
          <button
            type="button"
            className="isd-pending-banner-cancel"
            onClick={handleDraftCancel}
            title="Cancel placement"
            aria-label="Cancel placement"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      )}

      {/* Drawing tools (Move / Measure) live as a floating pill at the top
       * of the map now — see .path-map-intersection-tools. */}

      {/* ── Lane/path picker + Create button ── */}
      {!pendingIsdDraft && !formOpen && (
        <div className="isd-target-picker">
          <div className="isd-target-picker-header">
            <span className="isd-target-picker-label">
              Select a lane to associate the ISD mark with (XML files are
              generated per lane; paths are shown as headers only)
            </span>
            <button
              type="button"
              className="isd-target-picker-refresh-btn"
              onClick={() => onRefreshPathsAndLanes?.()}
              title="Refresh the list of paths and lanes"
              aria-label="Refresh paths and lanes"
            >
              <i className="fa-solid fa-rotate-right" aria-hidden />
            </button>
          </div>
          {targets.length === 0 ? (
            <p className="isd-empty-hint">
              No paths or lanes yet — create one in the Path Creator panel
              first.
            </p>
          ) : (
            <ul
              className="isd-target-picker-list"
              role="listbox"
              aria-label="Paths and lanes"
            >
              {targets.map((t) => {
                const isLane = t.kind === "lane";
                const isSel = isLane && t.id === selectedTargetId;
                // Paths render as non-selectable headers — only lanes can be
                // chosen as the ISD target, since XML emission is per-lane.
                return (
                  <li
                    key={t.id}
                    role={isLane ? "option" : "presentation"}
                    aria-selected={isLane ? isSel : undefined}
                    className={`isd-target-picker-item${
                      isLane
                        ? " isd-target-picker-item--lane"
                        : " isd-target-picker-item--header"
                    }${isSel ? " isd-target-picker-item--selected" : ""}`}
                    onClick={isLane ? () => setSelectedTargetId(t.id) : undefined}
                    onKeyDown={
                      isLane
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedTargetId(t.id);
                            }
                          }
                        : undefined
                    }
                    tabIndex={isLane ? 0 : -1}
                  >
                    <i
                      className={`fa-solid ${
                        isLane
                          ? "fa-arrows-left-right-to-line"
                          : "fa-route"
                      }`}
                      aria-hidden
                    />
                    <span className="isd-target-picker-item-label">
                      {t.label}
                    </span>
                    <button
                      type="button"
                      className="isd-target-picker-eye-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isLane) onToggleLaneVisible?.(t.id);
                        else onTogglePathVisible?.(t.id);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      title={t.visible ? `Hide ${t.kind}` : `Show ${t.kind}`}
                      aria-label={
                        t.visible
                          ? `Hide ${t.kind} ${t.label}`
                          : `Show ${t.kind} ${t.label}`
                      }
                      aria-pressed={!t.visible}
                    >
                      <i
                        className={`fas ${t.visible ? "fa-eye" : "fa-eye-slash"}`}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {selectedTarget && (
            <button
              type="button"
              className="isd-create-btn"
              onClick={openForm}
              title="Open the ISD mark creation form"
            >
              <i className="fa-solid fa-circle-dot" aria-hidden />
              Create ISD mark
            </button>
          )}
        </div>
      )}

      {/* ── Creation form ── */}
      {formOpen && (
        <form
          className="isd-creation-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleFormConfirm();
          }}
        >
          <div className="isd-creation-form-title">RSA ISD Cell Options</div>

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-form-name">
              Intersection Number (Name)
            </label>
            <input
              id="isd-form-name"
              type="text"
              className="isd-properties-input"
              value={formIntersectionName}
              onChange={(e) => setFormIntersectionName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-form-mvr">
              Maneuver Number (1,2,3,...)
            </label>
            <input
              id="isd-form-mvr"
              type="number"
              min={1}
              step={1}
              className="isd-properties-input isd-properties-input--narrow"
              value={formManeuverIndex}
              onChange={(e) =>
                setFormManeuverIndex(Number(e.target.value) || 1)
              }
            />
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label">RSA Path Name</label>
            <span className="isd-properties-readonly">
              {selectedTarget?.label ?? "—"}
            </span>
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-form-case">
              Maneuver Case
            </label>
            <select
              id="isd-form-case"
              className="isd-properties-select"
              value={formIsdCase}
              onChange={(e) => setFormIsdCase(e.target.value)}
            >
              {Object.entries(ISD_CASE_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>
                  {lbl}
                </option>
              ))}
            </select>
          </div>

          {formIsdCase === ISD_CASES.F_CASE && (
            <div className="isd-properties-field">
              <label className="isd-properties-label">
                Opposite Point (F case)
              </label>
              <div className="isd-form-opposite-row">
                <button
                  type="button"
                  className="isd-form-select-btn"
                  onClick={() => {
                    if (oppositePointPickActive) {
                      onCancelOppositePointPick?.();
                      return;
                    }
                    onStartOppositePointPick?.((block) => {
                      onUpdateOppositeBlockDraft?.(block);
                    });
                  }}
                  title={
                    oppositePointPickActive
                      ? "Cancel — click-drag to pick a different opposite block"
                      : "Click and drag on the map to place the opposite block"
                  }
                >
                  {oppositePointPickActive
                    ? "Click-drag on map…"
                    : oppositeBlockDraft?.insertionPoint
                      ? "Change"
                      : "Select"}
                </button>
                <span className="isd-properties-coords isd-form-opposite-coords">
                  {oppositeBlockDraft?.insertionPoint
                    ? `${oppositeBlockDraft.insertionPoint.lat.toFixed(6)}, ${oppositeBlockDraft.insertionPoint.lng.toFixed(6)}`
                    : "Optional"}
                </span>
              </div>
            </div>
          )}

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-form-minor">
              Minor Road Name
            </label>
            <input
              id="isd-form-minor"
              type="text"
              className="isd-properties-input"
              value={formSecondaryRoute}
              onChange={(e) => setFormSecondaryRoute(e.target.value)}
            />
          </div>

          <div className="isd-creation-form-actions">
            <button
              type="submit"
              className="isd-form-ok-btn"
              disabled={!isFormValid}
              title={
                isFormValid
                  ? "Confirm and place on map"
                  : "Fill all fields to continue"
              }
            >
              OK
            </button>
            <button
              type="button"
              className="isd-form-cancel-btn"
              onClick={handleFormCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* ── ISD Points list ── */}
      <div className="paths-panel-drawn-shapes">
        <div className="paths-panel-drawn-shapes-header">
          <span className="paths-panel-drawn-shapes-title">
            ISD Points{intersections.length > 0 && ` (${intersections.length})`}
          </span>
          {intersections.length > 0 && (
            <div className="paths-panel-drawn-shapes-actions">
              <button
                type="button"
                className="paths-panel-drawn-copy-all-btn"
                onClick={handleCopyAll}
                title="Copy all ISD points as JSON"
              >
                {copiedId === "all" ? (
                  <i className="fas fa-check" />
                ) : (
                  <i className="fas fa-copy" />
                )}
                {copiedId === "all" ? " Copied" : " Copy all"}
              </button>
            </div>
          )}
        </div>

        {intersections.length === 0 ? (
          <p className="isd-empty-hint">No ISD points yet.</p>
        ) : (
          <ul
            className="paths-panel-drawn-list"
            aria-label="ISD intersection points"
          >
            {intersections.map((isd) => (
              <li
                key={isd.id}
                className={`paths-panel-drawn-item isd-point-item ${isd.id === activeIntersectionId ? "isd-point-item--active" : ""}`}
                onClick={() => onSetActiveIntersection?.(isd.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    onSetActiveIntersection?.(isd.id);
                }}
              >
                <div className="paths-panel-drawn-item-header">
                  <span className="isd-point-item-label">
                    {isd.intersectionName ||
                      `Intersection ${isd.maneuverIndex}`}
                  </span>
                  <span className="isd-point-item-case">
                    {ISD_CASE_LABELS[isd.isdCase] || isd.isdCase}
                  </span>
                  <button
                    type="button"
                    className="paths-panel-drawn-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteIntersection?.(isd.id);
                    }}
                    title="Delete this ISD point"
                    aria-label={`Delete intersection ${isd.intersectionName || isd.maneuverIndex}`}
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden />
                  </button>
                </div>
                {isd.insertionPoint && (
                  <pre className="paths-panel-drawn-item-coords">
                    lat: {isd.insertionPoint.lat?.toFixed(6)}, lng:{" "}
                    {isd.insertionPoint.lng?.toFixed(6)}
                  </pre>
                )}
                {isd.isdCase === ISD_CASES.F_CASE &&
                  isd.oppositePoint?.insertionPoint && (
                    <pre className="paths-panel-drawn-item-coords paths-panel-drawn-item-coords--opposite">
                      opposite — lat:{" "}
                      {isd.oppositePoint.insertionPoint.lat?.toFixed(6)}, lng:{" "}
                      {isd.oppositePoint.insertionPoint.lng?.toFixed(6)}
                    </pre>
                  )}
                {isd.id === activeIntersectionId && (
                  <button
                    type="button"
                    className="isd-point-item-properties-toggle"
                    aria-expanded={propertiesOpen}
                    aria-controls="isd-properties-panel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPropertiesOpen((v) => !v);
                    }}
                    title={
                      propertiesOpen
                        ? "Collapse properties"
                        : "Expand properties"
                    }
                  >
                    <i
                      className={`fa-solid ${propertiesOpen ? "fa-chevron-down" : "fa-chevron-right"}`}
                      aria-hidden
                    />
                    <span>Properties</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Properties panel (active intersection edits) ── */}
      {activeIntersection && propertiesOpen && (
        <div className="isd-properties-panel" id="isd-properties-panel">
          <div className="isd-properties-field">
            <label
              className="isd-properties-label"
              htmlFor="isd-intersection-name"
            >
              INTERSECTION
            </label>
            <input
              id="isd-intersection-name"
              type="text"
              className="isd-properties-input"
              value={activeIntersection.intersectionName}
              onChange={(e) =>
                handleFieldChange("intersectionName", e.target.value)
              }
              placeholder="Name or number"
            />
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label">ID</label>
            <span className="isd-properties-readonly">
              {activeIntersection.maneuverIndex}
            </span>
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-case">
              CASE
            </label>
            <select
              id="isd-case"
              className="isd-properties-select"
              value={activeIntersection.isdCase}
              onChange={(e) => handleFieldChange("isdCase", e.target.value)}
            >
              {Object.entries(ISD_CASE_LABELS).map(([val, lbl]) => (
                <option key={val} value={val}>
                  {lbl}
                </option>
              ))}
            </select>
          </div>

          {activeIntersection.isdCase === ISD_CASES.F_CASE && (
            <div className="isd-properties-field">
              <label className="isd-properties-label">OPPOSITEP</label>
              <div className="isd-form-opposite-row">
                <button
                  type="button"
                  className="isd-form-select-btn"
                  onClick={() => {
                    if (oppositePointPickActive) {
                      onCancelOppositePointPick?.();
                      return;
                    }
                    onStartOppositePointPick?.((block) => {
                      onUpdateIntersection?.(activeIntersection.id, {
                        oppositePoint: block,
                      });
                    });
                  }}
                  title={
                    oppositePointPickActive
                      ? "Cancel — click-drag to pick a different opposite block"
                      : "Click and drag on the map to place the opposite block"
                  }
                >
                  {oppositePointPickActive
                    ? "Click-drag on map…"
                    : activeIntersection.oppositePoint?.insertionPoint
                      ? "Change"
                      : "Select"}
                </button>
                <span className="isd-properties-coords isd-form-opposite-coords">
                  {activeIntersection.oppositePoint?.insertionPoint
                    ? `${activeIntersection.oppositePoint.insertionPoint.lat?.toFixed(6)}, ${activeIntersection.oppositePoint.insertionPoint.lng?.toFixed(6)}`
                    : "Not set"}
                </span>
              </div>
            </div>
          )}

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-rsapath">
              RSAPATH
            </label>
            <select
              id="isd-rsapath"
              className="isd-properties-select"
              value={activeIntersection.rsaPathId ?? ""}
              onChange={(e) =>
                handleFieldChange("rsaPathId", e.target.value || null)
              }
            >
              <option value="">-- Select lane --</option>
              {paths.map((p) => {
                const pathLanes = lanes.filter((l) => l.pathId === p.id);
                if (pathLanes.length === 0) return null;
                return (
                  <optgroup key={p.id} label={p.label}>
                    {pathLanes.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>

          <div className="isd-properties-field">
            <label
              className="isd-properties-label"
              htmlFor="isd-secondary-route"
            >
              Secondary route
            </label>
            <input
              id="isd-secondary-route"
              type="text"
              className="isd-properties-input"
              value={activeIntersection.secondaryRoute}
              onChange={(e) =>
                handleFieldChange("secondaryRoute", e.target.value)
              }
              placeholder="Minor road name"
            />
          </div>

          <div className="isd-properties-field">
            <label className="isd-properties-label" htmlFor="isd-azimuth">
              Azimuth
            </label>
            <div className="isd-properties-azimuth-row">
              <input
                id="isd-azimuth"
                type="number"
                className="isd-properties-input isd-properties-input--narrow"
                value={
                  Number.isFinite(activeIntersection.azimuth)
                    ? Number(activeIntersection.azimuth).toFixed(2)
                    : "0.00"
                }
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) {
                    const rounded = Math.round(v * 100) / 100;
                    handleFieldChange(
                      "azimuth",
                      Math.max(0, Math.min(360, rounded)),
                    );
                  }
                }}
                min={0}
                max={360}
                step={0.01}
              />
              <span className="isd-properties-unit">deg</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Measure overlays ── */}
      {drawnOverlays.length > 0 && (
        <div className="paths-panel-drawn-shapes isd-measure-section">
          <div className="paths-panel-drawn-shapes-header">
            <span className="paths-panel-drawn-shapes-title">Measurements</span>
            <button
              type="button"
              className="paths-panel-drawn-clear-btn"
              onClick={() => onClearOverlays?.()}
              title="Clear all measurements"
            >
              <i className="fas fa-trash-alt" aria-hidden />
              Clear
            </button>
          </div>
          <ul
            className="paths-panel-drawn-list"
            aria-label="Measurement overlays"
          >
            {drawnOverlays.map((overlay) => (
              <li key={overlay.id} className="paths-panel-drawn-item">
                <div className="paths-panel-drawn-item-header">
                  <input
                    type="text"
                    className="paths-panel-drawn-item-type"
                    value={overlay.label ?? overlay.type}
                    onChange={(e) =>
                      onOverlayUpdate?.(overlay.id, { label: e.target.value })
                    }
                    aria-label={`Edit label for ${overlay.type}`}
                  />
                  <button
                    type="button"
                    className="paths-panel-drawn-item-delete"
                    onClick={() => onOverlayRemove?.(overlay.id)}
                    title="Delete this measurement"
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden />
                  </button>
                </div>
                <pre className="paths-panel-drawn-item-coords">
                  {overlay.coordinates?.map((p, i) => (
                    <span key={i}>
                      [{i}] {p.lat}, {p.lng}
                      {i < overlay.coordinates.length - 1 ? "\n" : ""}
                    </span>
                  ))}
                </pre>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
